import Mustache from 'mustache';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import localizations from './localizations';
import breakpoints from '../breakpoints';

/**
 * Search vehicles from an ajax endpoint and render them via mustache
 *
 * @class VehicleSearch
 */
class VehicleSearch {
  constructor() {
    this.vehicleSearchForm = document.getElementById('vehicle-search-form');
    this.vehicleSearchSidebar = document.querySelector(
      '.vehicle-search__sidebar'
    );
    this.cardListWrapper = document.querySelector('.card-list--wrapper');
    this.loadingSpinner = document.getElementById('vehicle-search-spinner');
    this.orderControl = document.querySelector('.js-vehicle-search-sort');
    this.nextPageButton = document.querySelector('.js-vehicle-search-next-page');
    this.prevPageButton = document.querySelector('.js-vehicle-search-prev-page');
    this.mobileResultAmount = document.querySelector('.js-vehicle-search-results-amount');
    this.resultAmount = document.querySelector('.js-vehicle-search-count');
    this.seoText = document.querySelector('.vehicle-search__seo-text');
    this.tabTriggers = document.querySelectorAll('.js-trigger-filter-tabs');
    this.modelFilterTrigger = document.querySelector('.js-filter-models');
    this.resetButtons = document.querySelectorAll('button[type="reset"]');

    this.formTimeOutId = null;

    this.templateVehicleList = document.getElementById('mustache-vehicle-list').innerHTML;
    this.templateVehicleCard = document.getElementById('mustache-vehicle-card').innerHTML;
    this.templateVehicleListHeading = document.getElementById('mustache-vehicle-list-heading').innerHTML;
    this.templateAdCard = document.getElementById('mustache-ad-card').innerHTML;


    this.seoBrands = JSON.parse(
      this.vehicleSearchForm.querySelector('#seo-brands').innerText
    );

    // Hopefully all multiselect are now ready.
    this.updateHistoryState = this.updateHistoryState.bind(this);
    window.addEventListener('load', this.updateHistoryState);

    this.isUrlChanged = false;

    if (sessionStorage.getItem('filtersOpen') === 'true') {
      this.openFilters();
    }
  }

  /**
   * Update history state with search parameters
   */
  updateHistoryState() {

    // Prevent browser from restoring scroll position. Customer wants the page scroll to the top
    // when the search is performed and page reloaded.
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }


    // This includes make, fuel and bodytype slugs for the seo pages
    const seoSlugs = this.seoBrands.map((brand) => brand.slug);

    const currentPathname = window.location.pathname;
    const nextUrl = new URL(window.location.origin);
    nextUrl.pathname = '/vaihtoautot/';

    const formData = new FormData(this.vehicleSearchForm);

    const makeSelected = formData.getAll('make');
    const fuelSelected = formData.getAll('fuel[]');
    const bodyTypeSelected = formData.getAll('body_type[]');

    // If only hybrid and/or plug-in-hybrid (and possibly also all hybrids) is selected,
    // url pathname is updated to "hybridiautot"
    if (!makeSelected.length &&
      !bodyTypeSelected.length &&
      fuelSelected.length > 0 ) {

      let onlyHybrid = true;

      for (const [field, value] of formData.entries()) {

        if (field && value && value !== 'plug-in-hybridi' && value !== 'hybridi' && value !== 'kaikki-hybridit') {
          onlyHybrid = false;
          break;
        }
      }
      // Check that not only all hybrids are selected, because that case will be handled separately
      onlyHybrid && (fuelSelected.includes('plug-in-hybridi') || fuelSelected.includes('hybridi')) ? nextUrl.pathname += 'hybridiautot/' : null;
    }

    // Convert fuel and body type fields to match the URL slugs
    const fuelParamsToslug = {
      'sähkö': 'sahkoautot',
      'kaikki-hybridit': 'hybridiautot',
    };
    const bodyParamsToSlug = {
      'farmari': 'farmariautot',
      'maastoauto': 'maastoautot',
      'pakettiauto': 'pakettiautot',
    };

    for (let [field, value] of formData.entries()) {
      if (field && value) {

        // If the make is selected, we add it to the URL and skip the rest.
        // In other cases, we check if the selected value is the only one selected
        // from fuel, body or vat type, and if it is, we add it to the URL and skip the rest
        if (makeSelected.length === 0) {
          if (field === 'fuel[]' && fuelParamsToslug[value] && fuelSelected.length === 1 && !bodyTypeSelected.length) {
            value = fuelParamsToslug[value];
          }
          else if (field === 'body_type[]' && bodyParamsToSlug[value] && bodyTypeSelected.length === 1 && !fuelSelected.length) {
            value = bodyParamsToSlug[value];
          }
          else if (field === 'vat_deductible' && value === '1' && !bodyTypeSelected.length && !fuelSelected.length) {
            value = 'alv-vahennyskelpoiset-autot';
          }
        }

        // If the value is one of the "special ones" that has its own page in Stage,
        // we update the value to the URL pathname
        if (seoSlugs.includes(value?.toLowerCase())) {
          nextUrl.pathname += `${value?.toLowerCase()}/`;
        } else {
          // otherwise we put it in the params as usual, unless the field is 'price-tab' which is excluded
          if (field !== 'price-tab') {
            nextUrl.searchParams.append(field, value);
          }
        }
      }
    }

    // Special case for family cars, because it includes multiple body type parameters
    if (bodyTypeSelected.length === 3 &&
      bodyTypeSelected.includes('farmari') &&
      bodyTypeSelected.includes('maastoauto') &&
      bodyTypeSelected.includes('tila-auto') &&
      !fuelSelected.length &&
      !makeSelected.length ) {

      nextUrl.pathname = '/vaihtoautot/perheautot/';
      nextUrl.searchParams.delete('body_type[]');
    }

    // Add page parameter to query string so that is found when a popState event is triggered.
    // Fixes back button functionality, user end up right page when back button is pressed.
    const hrefUrl = new URL(window.location.href);

    if (hrefUrl.searchParams.get('page')) {
      nextUrl.searchParams.append('page', hrefUrl.searchParams.get('page'));
    }
    // same thing for order parameter
    if (hrefUrl.searchParams.get('order')) {
      nextUrl.searchParams.append('order', hrefUrl.searchParams.get('order'));
    }

    const autoOpenFilters = hrefUrl.searchParams.get('filtersopen');
    const inTabletBreakpoint = window.innerWidth <= breakpoints.md;
    if (autoOpenFilters && inTabletBreakpoint) {
      this.openFilters();
    }

    const initialHistoryState = history.state;
    this.isUrlChanged = currentPathname !== nextUrl.pathname;

    history.pushState(
      { queryString: nextUrl.toString() },
      null,
      nextUrl.toString()
    );

    // If there is no history state, it means user came from front page hero element.
    // In case the user landed to the SEO page, we need to reload the page.
    // (https://crasman.atlassian.net/browse/BILARWEB-471)
    if (!initialHistoryState && this.isUrlChanged) {
      location.reload();
    }
  }

  /**
   * Serialize form element into query string
   *
   * @param {*} form form element
   * @return {string} query string
   * @memberof VehicleSearch
   */
  _serializeForm(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [field, value] of formData.entries()) {
      params.append(field, value);
    }

    return { queryString: params.toString() };
  }

  /**
   * Empty vehicle list so new vehicles can be shown
   *
   * @memberof VehicleSearch
   */
  _clearVehicleList() {
    this.loadingSpinner.classList.remove('d-none');
    this.cardListWrapper.innerHTML = '';

    document.getElementById('vehicle-search-seo')?.remove();
  }

  /**
   * Get vehicles from ajax endpoint with selected form filters
   *
   * @param {object} form vehicle search form element
   * @returns {object} request response or rejected promise
   * @memberof VehicleSearch
   */
  _getVehicles(form) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const { queryString } = this._serializeForm(form);

      request.open('GET', '/stage-ajax/getVehicles/?' + queryString, true);
      request.send();

      request.onload = () => {
        if (request.readyState === 4 && request.status === 200) {
          // update search watcher link based on obtained query string
          const searchWatcher = document.getElementsByClassName(
            'save-search-watcher'
          )[0];
          if (searchWatcher) {
            if (searchWatcher.hasAttribute('href')) {
              searchWatcher.setAttribute(
                'href',
                searchWatcher.getAttribute('href').split('?')[0] + queryString
              );
            }
          }

          resolve(request.response);
        } else reject(request);
      };

      request.onerror = () => reject(request);
    });
  }

  /**
   * Render ajax response as html with mustache
   *
   * @async
   * @param {object} response raw json from ajax
   * @memberof VehicleSearch
   */
  async _renderVehicles(response) {
    response = JSON.parse(response);
    this.loadingSpinner.classList.add('d-none');

    const cardListTitle = document.querySelector('.card-list--title');

    const titleData = {
      count: response.paging.totalResults,
    };

    titleData.heading = response?.title?.specificTitle ? `${response.title.specificTitle} - ${window.titles?.generalTitle}` : window.titles?.generalTitle;
    document.title = titleData.heading;

    const carSearchPageId = window.titles.pageTitles.id;
    const carSearchSeoText = await this._getSeoTexts(carSearchPageId);

    // Update seo text to match text of Vaihtoautot page
    this.seoText.innerHTML = carSearchSeoText || '';

    cardListTitle.outerHTML = Mustache.render(
      this.templateVehicleListHeading,
      titleData
    );

    if (localizations.page_title) document.title = localizations.page_title;
    this.mobileResultAmount.innerHTML = `(${response.paging.totalResults})`;
    this.cardListWrapper.innerHTML = Mustache.render(
      this.templateVehicleList,
      response,
      { vehicleCard: this.templateVehicleCard, adCard: this.templateAdCard }
    );
  }

  /**
   * Show error message to user
   *
   * @param {*} error error message
   * @memberof VehicleSearch
   */
  _renderError(error) {
    this.loadingSpinner.classList.add('d-none');
    this.cardListWrapper.innerHTML = `<h3 class="error-text">${localizations.general_error}</h3>`;
    console.log(error);
  }

  /**
   * Get seo text of stage page from ajax endpoint
   *
   * @async
   * @param {string} stageId - id of Stage page
   * @returns {object} response json
   */
  async _getSeoTexts(stageId) {
    const url = `/stage-ajax/getVehicleSearchSeoText?stageId=${stageId}`;

    try {
      const response =  await fetch(url, {
        method: 'GET',
      });

      return response.json();
    } catch (error) {
      console.warn('Error loading seo texts', error);
    }
  }

  /**
   * Create event related actions for vehicle search elements
   *
   * @memberof VehicleSearch
   */
  initFilters() {
    this.toggleResetButtonVisibility();

    this.vehicleSearchForm.addEventListener('submit', (e) => {
      e.preventDefault();

      this.updateHistoryState();
      this.toggleResetButtonVisibility()

      // If the url hasn't changed (means there is no special SEO page),
      // update vehicles dynamically, otherwise reload the page to get the correct data.
      // Client requested that the page should be reloaded when the seo page is opened.
      // (https://crasman.atlassian.net/browse/BILARWEB-471)
      if (!this.isUrlChanged) {
        this._clearVehicleList();

        this._getVehicles(e.target)
          .then((vehicleData) => {
            this._renderVehicles(vehicleData);
          })
          .catch((errorMessage) => {
            this._renderError(errorMessage);
          });
      } else {
        location.reload();
      }
    });

    //dispatch change event to trigger js for multiselect fields.
    this.vehicleSearchForm.addEventListener('reset', (e) => {
      setTimeout(() => {
        this.deletePageSearchParams();
        //wait a few ms and then trigger change event to make sure that reset event has finished.
        const selectFields = this.vehicleSearchForm.querySelectorAll('select');
        const checkboxFields = this.vehicleSearchForm.querySelectorAll('input[type="checkbox"]');
        const changeEvent = new Event('change');

        selectFields.forEach((field) => {
          const options = field.querySelectorAll('option');
          options.forEach((option) => (option.selected = false));
          field.dispatchEvent(changeEvent);
        });

        checkboxFields.forEach((checkbox) => {
          checkbox.checked = false;
          checkbox.dispatchEvent(changeEvent);
        });

        this.vehicleSearchForm.dispatchEvent(changeEvent);
      }, 5);
    });

    //trigger search after filter change (with buffer).
    this.vehicleSearchForm.addEventListener('change', (e) => {
      this.deletePageSearchParams();
      // We don't want to trigger search when user is changing the price tab
      if (e.target.name !== 'price-tab') {
        clearTimeout(this.formTimeOutId);
        this.mobileResultAmount.innerHTML = '';
        this.resultAmount.innerHTML = '';

        this.formTimeOutId = setTimeout(() => {
          const event = new Event('submit', { cancelable: true });
          this.vehicleSearchForm.dispatchEvent(event);
        }, 1000);
      }
    });

    const minMthPriceSelect = document.querySelector(
      '[name="min_price_monthly"]'
    );
    const minPriceSelect = document.querySelector('[name="min_price"]');
    const maxMthPriceSelect = document.querySelector(
      '[name="max_price_monthly"]'
    );
    const maxPriceSelect = document.querySelector('[name="max_price"]');

    const changeSelected = (elem) => {
      elem.selectedIndex = -1;
      elem.value = '';
      const changeEvent = new Event('change');
      elem.dispatchEvent(changeEvent);
    };

    minMthPriceSelect.addEventListener('change', (elem) => {
      if (elem.target.value) changeSelected(minPriceSelect);
    });

    minPriceSelect.addEventListener('change', (elem) => {
      if (elem.target.value) changeSelected(minMthPriceSelect);
    });

    maxMthPriceSelect.addEventListener('change', (elem) => {
      if (elem.target.value) changeSelected(maxPriceSelect);
    });

    maxPriceSelect.addEventListener('change', (elem) => {
      if (elem.target.value) changeSelected(maxMthPriceSelect);
    });

    //show more filters button
    const showMoreFiltersButton = document.querySelector(
      '.js-vehicle-search-show-more-filters'
    );
    const additionalFiltres = document.querySelector(
      '.vehicle-search__additional-filters'
    );
    if (showMoreFiltersButton && additionalFiltres) {
      showMoreFiltersButton.addEventListener('click', (e) => {
        additionalFiltres.classList.remove('d-none');
        showMoreFiltersButton.classList.add('d-none');
      });
    }

    //add js to ordering control select input as it's placed outside the html form element
    this.orderControl.addEventListener('change', (e) => {
      const orderInput = this.vehicleSearchForm.querySelector(
        'input[name="order"]'
      );
      orderInput.value = e.target.value;
      const event = new Event('change');
      this.vehicleSearchForm.dispatchEvent(event);
    });

    //hide and show filters for mobile users
    const searchFilterToggle = document.querySelectorAll('.js-toggle-vehicle-search-filters');

    searchFilterToggle.forEach((button) => {
      button.addEventListener('click', (e) => {
        if (this.filtersOpen()) {
          this.closeFilters();
          history.pushState({ filterOpen: true }, null, '');
        } else {
          this.openFilters();
          history.pushState({ filterOpen: false }, null, '');
        }
      });
    });

    const fuelFilter = document.querySelector('[name="fuel[]"]');
    const evFilters = document.querySelector('#ev-filters');
    const evFuels = JSON.parse( evFilters.dataset.evFuels );

    fuelFilter.addEventListener('change', () => {
      const show = [ ...document.querySelector('[name="fuel[]"]').options ]
        .filter( option => option.selected )
        .some( option => evFuels.includes(option.value.toLowerCase()) );

      evFilters.classList.toggle('d-none', !show);
    });

    //handle history related state changes.
    window.onpopstate = (e) => {
      if (!e.state) return false;

      //hide filters on mobile on back press.
      if (typeof e.state.filterOpen === 'undefined' && this.filtersOpen()) {
        this.closeFilters();
      }
      if (e.state.filterOpen === true && this.filtersOpen()) {
        this.closeFilters();
      } else if (e.state.filterOpen === false) {
        this.openFilters();
      }

      //handle vehicle search history push
      if (e.state.queryString) {
        window.location.href = e.state.queryString;
      }
    };

    if (this.tabTriggers.length) {
      //price tab functionality
      this.tabTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) =>
          this.handleTabTriggerClick(event.currentTarget)
        );
      });
      this.preselectTab();
    }
  }

  handleTabTriggerClick(trigger) {
    const container = trigger.closest('.vehicle-search-filter-tabs');
    const targetElement = container.querySelector(
      '#' + trigger.dataset.targetId
    );
    const filterItems = container.querySelectorAll(
      '.vehicle-search-filter-tabs__item'
    );
    filterItems.forEach((element) => element.classList.add('d-none'));
    targetElement?.classList.remove('d-none');
    this.tabTriggers.forEach((t) => t.classList.remove('active'));
    trigger.classList.add('active');
  }

  /**
   * Opens the prices tab, when either min_price or max_price filter is applied
   * otherwise defaults to monthly prices tab.
   */
  preselectTab() {
    const minPriceSelect = this.vehicleSearchForm.querySelector(
      'select[name="min_price"]'
    );
    const maxPriceSelect = this.vehicleSearchForm.querySelector(
      'select[name="max_price"]'
    );
    const activeTabId =
      minPriceSelect?.value || maxPriceSelect?.value
        ? 'filters-price'
        : 'filters-price-monthly';
    const activeTabTrigger = [...this.tabTriggers].find(
      (trigger) => trigger.dataset.targetId === activeTabId
    );
    this.handleTabTriggerClick(activeTabTrigger);
  }

  /**
   * Open filter element for mobile users
   *
   * @memberof VehicleSearch
   */
  openFilters() {
    this.vehicleSearchSidebar.classList.add('open');
    this.vehicleSearchSidebar.scrollTop = 0;
    sessionStorage.setItem('filtersOpen', true);
    disableBodyScroll(this.vehicleSearchSidebar);
  }

  /**
   * Close filter element for mobile users
   *
   * @memberof VehicleSearch
   */
  closeFilters() {
    this.vehicleSearchSidebar.classList.remove('open');
    sessionStorage.setItem('filtersOpen', false);
    enableBodyScroll(this.vehicleSearchSidebar);
  }

  /**
   *
   *
   * @return {boolean} is filter element open or no
   * @memberof VehicleSearch
   */
  filtersOpen() {
    return this.vehicleSearchSidebar.classList.contains('open');
  }

  /**
   * Delete page search parameters from url if they exist
   * @memberof VehicleSearch
   * @return {void}
   */
  deletePageSearchParams() {
    const url = new URL(window.location.href);
    if (url.searchParams.has('page')) {
      url.searchParams.delete('page');
      history.pushState({}, '', url.toString());
    }
  }

  /**
   * Toggle visibility of reset buttons based on form data
   * This function checks if there are any parameters in the form data that is not 'price-tab'
   * and shows or hides the reset buttons accordingly.
   * @memberof VehicleSearch
   * @return {void}
   */
  toggleResetButtonVisibility() {
    const formData = new FormData(this.vehicleSearchForm);
    // Check if there are any parameters in the form data that is not price-tab
    const hasParams = Array.from(formData.entries()).some(([key, value]) => value && key !== 'price-tab');
    this.resetButtons.forEach((resetButton) => {
      resetButton.style.display = hasParams ? 'inline-flex' : 'none';
    });
  }

  /**
   * Initialize vehicle search
   *
   * @memberof VehicleSearch
   */
  init() {
    this.initFilters();
  }
}

export default new VehicleSearch();
